import type { BookingRecordModel } from "../Models/BookingModels";
import type {
  CheckoutSessionResponseModel,
  PaymentRecordModel,
  PaymentStatusModel,
  PaymentSummaryModel,
} from "../Models/PaymentModels";
import {
  AppService,
  safeJsonParseFn,
  safeJsonStringifyFn,
  type HttpClientModel,
  type Result,
} from "./App.service";
import { BookingService } from "./Booking.service";
import {
  buildCheckoutSessionRequestFn,
  buildMockCheckoutSessionFn,
} from "../../Features/Payments/Functions";

const paymentStorageKey = "cmj-cinema:payments";

const readPaymentsFromStorageFn = (): PaymentRecordModel[] => {
  if (typeof window === "undefined") {
    return [];
  }

  return (
    safeJsonParseFn<PaymentRecordModel[]>(
      window.localStorage.getItem(paymentStorageKey)
    ) ?? []
  );
};

const writePaymentsToStorageFn = (payments: PaymentRecordModel[]) => {
  if (typeof window === "undefined") {
    return;
  }

  const serialized = safeJsonStringifyFn(payments);
  if (serialized) {
    window.localStorage.setItem(paymentStorageKey, serialized);
  }
};

const upsertPaymentFn = (payment: PaymentRecordModel) => {
  const currentPayments = readPaymentsFromStorageFn();
  const nextPayments = [
    payment,
    ...currentPayments.filter((item) => item.id !== payment.id),
  ];
  writePaymentsToStorageFn(nextPayments);
};

const buildPaymentRecordFn = (
  booking: BookingRecordModel,
  session: CheckoutSessionResponseModel
): PaymentRecordModel => {
  const timestamp = new Date().toISOString();
  return {
    id: `payment-${Math.random().toString(36).slice(2, 10)}`,
    bookingId: booking.id,
    amount: session.amount,
    currency: session.currency,
    paymentOption: booking.paymentOption,
    provider: session.provider,
    providerUrl: session.checkoutUrl,
    sessionId: session.sessionId,
    status: "checkout_started",
    createdAt: timestamp,
    updatedAt: timestamp,
  };
};

export const StripeService = {
  async createCheckoutSession(
    booking: BookingRecordModel,
    client: HttpClientModel = AppService.httpClient
  ): Promise<Result<CheckoutSessionResponseModel>> {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "http://localhost:9000";
    const request = buildCheckoutSessionRequestFn(booking, origin);

    try {
      const response = await client.post("/api/payments/stripe/checkout-session", request);
      const session = {
        ...buildMockCheckoutSessionFn(request),
        ...(response.data as Partial<CheckoutSessionResponseModel>),
        provider: "stripe" as const,
      };
      upsertPaymentFn(buildPaymentRecordFn(booking, session));
      BookingService.updateBookingStatus(booking.id, "checkout_started");
      return { ok: true, data: session };
    } catch {
      const fallbackSession = buildMockCheckoutSessionFn(request);
      upsertPaymentFn(buildPaymentRecordFn(booking, fallbackSession));
      BookingService.updateBookingStatus(booking.id, "checkout_started");
      return { ok: true, data: fallbackSession };
    }
  },

  getPaymentByBookingId(bookingId: string) {
    return readPaymentsFromStorageFn().find((item) => item.bookingId === bookingId) ?? null;
  },

  reconcileCheckoutResult(
    bookingId: string,
    sessionId: string,
    status: Extract<PaymentStatusModel, "completed" | "cancelled">
  ): PaymentSummaryModel {
    const payment = this.getPaymentByBookingId(bookingId);

    if (payment) {
      const nextPayment = {
        ...payment,
        sessionId: sessionId || payment.sessionId,
        status,
        updatedAt: new Date().toISOString(),
      };
      upsertPaymentFn(nextPayment);
    }

    BookingService.updateBookingStatus(
      bookingId,
      status === "completed" ? "confirmed" : "cancelled"
    );

    return {
      booking: BookingService.getBookingById(bookingId),
      payment: this.getPaymentByBookingId(bookingId),
    };
  },
};

export default StripeService;
